import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';

function Confetti() {
  const pieces = Array.from({ length: 60 });
  const colors = ['#f97316', '#ec4899', '#f59e0b', '#ef4444', '#10b981'];
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-10">
      {pieces.map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-3 rounded-sm"
          style={{
            left: `${Math.random() * 100}%`,
            top: `-${Math.random() * 20}px`,
            background: colors[Math.floor(Math.random() * colors.length)],
            animation: `confettiFall ${2 + Math.random() * 3}s linear ${Math.random() * 2}s forwards`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}
    </div>
  );
}

export default function OrderConfirmationPage() {
  const [searchParams] = useSearchParams();
  const [showConfetti, setShowConfetti] = useState(true);

  const orderNum = searchParams.get('order') || `AIS-${Math.floor(100000 + Math.random() * 900000)}`;
  const total = searchParams.get('total') || '0';
  const delivery = searchParams.get('delivery') || '3-5 business days';
  const customerName = searchParams.get('name') || '';
  const phone = searchParams.get('phone') || '';

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const whatsappMsg = encodeURIComponent(`Hi! I'd like to track my order: ${orderNum}${customerName ? `\nName: ${customerName}` : ''}`);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30 relative">
      {showConfetti && <Confetti />}
      <Header />

      <div className="pt-24 pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-28 h-28 flex items-center justify-center mx-auto mb-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 animate-bounceIn">
            <i className="ri-check-line text-5xl text-white"></i>
          </div>

          <h1 className="text-4xl font-black text-gray-800 mb-3">Order Confirmed!</h1>
          <p className="text-gray-500 text-lg mb-2">Thank you{customerName ? `, ${customerName}` : ''} for shopping with AiShopio</p>
          <p className="text-gray-400 text-sm mb-8">A confirmation email has been sent to your inbox</p>

          <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 mb-6 overflow-hidden">
            <p className="text-sm text-gray-500 mb-1">Order Number</p>
            <p className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent mb-4 break-all">
              {orderNum}
            </p>
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="text-center p-2 sm:p-3 rounded-2xl bg-orange-50 overflow-hidden">
                <i className="ri-calendar-line text-orange-500 text-lg sm:text-xl mb-1 block"></i>
                <p className="text-xs text-gray-500">Order Date</p>
                <p className="text-xs sm:text-sm font-bold text-gray-800">Today</p>
              </div>
              <div className="text-center p-2 sm:p-3 rounded-2xl bg-green-50 overflow-hidden">
                <i className="ri-truck-line text-green-500 text-lg sm:text-xl mb-1 block"></i>
                <p className="text-xs text-gray-500">Est. Delivery</p>
                <p className="text-xs sm:text-sm font-bold text-gray-800 line-clamp-2">{delivery}</p>
              </div>
              <div className="text-center p-2 sm:p-3 rounded-2xl bg-pink-50 overflow-hidden">
                <i className="ri-money-dollar-circle-line text-pink-500 text-lg sm:text-xl mb-1 block"></i>
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-xs sm:text-sm font-bold text-gray-800">AED {total}</p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl p-4 sm:p-6 mb-6 text-left overflow-hidden" style={{ background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)', border: '2px solid #86efac' }}>
            <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl flex-shrink-0" style={{ background: '#25d366' }}>
                <i className="ri-whatsapp-line text-white text-2xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="font-black text-gray-800 text-lg mb-1">Track via WhatsApp</h3>
                <p className="text-gray-600 text-sm mb-3">Get real-time updates on your order. Our team will confirm within minutes!</p>
                <a
                  href={`https://wa.me/${phone ? phone.replace(/\D/g, '') : '971500000000'}?text=${whatsappMsg}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-white font-semibold text-sm sm:text-base hover:opacity-90 transition-all cursor-pointer overflow-hidden"
                  style={{ background: '#25d366' }}
                >
                  <i className="ri-whatsapp-line text-lg flex-shrink-0"></i>
                  <span className="truncate">
                    <span>Chat on WhatsApp</span>
                    <span className="hidden sm:inline"> — +971 50 000 0000</span>
                  </span>
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 mb-8">
            <h3 className="font-bold text-gray-800 mb-4">What happens next?</h3>
            <div className="space-y-3">
              {[
                { icon: 'ri-check-circle-line', color: 'text-green-500', title: 'Order Confirmed', desc: 'Your order has been received and is being processed', done: true },
                { icon: 'ri-box-3-line', color: 'text-orange-500', title: 'Packing', desc: 'Your items are being carefully packed', done: false },
                { icon: 'ri-truck-line', color: 'text-gray-400', title: 'Out for Delivery', desc: 'Your order is on its way to you', done: false },
                { icon: 'ri-home-smile-line', color: 'text-gray-400', title: 'Delivered', desc: 'Enjoy your purchase!', done: false },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <i className={`${s.icon} ${s.color} text-xl flex-shrink-0`}></i>
                  <div className="flex-1 text-left">
                    <p className={`text-sm font-bold ${s.done ? 'text-gray-800' : 'text-gray-400'}`}>{s.title}</p>
                    <p className="text-xs text-gray-400">{s.desc}</p>
                  </div>
                  {s.done && <i className="ri-check-line text-green-500 text-sm"></i>}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/track-order" className="w-full sm:w-auto px-8 py-4 rounded-full border-2 border-orange-500 text-orange-500 font-bold hover:bg-orange-50 transition-all cursor-pointer text-center">
              <i className="ri-map-pin-line mr-2"></i>Track Order
            </Link>
            <Link to="/shop" className="w-full sm:w-auto px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold hover:scale-105 hover:shadow-lg hover:shadow-orange-500/30 transition-all cursor-pointer text-center">
              <i className="ri-store-line mr-2"></i>Continue Shopping
            </Link>
          </div>
        </div>
      </div>

      <Footer />
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
